import React, { useState, useMemo } from "react";
import {
  Search,
  Check,
  AlertTriangle,
  ArrowRight,
  X,
  ChevronLeft,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import {
  usePlanSearch,
  usePatchConfigMap,
} from "@features/onboarding/utility-setup/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";

export interface SamplePlan {
  id: string;
  name: string;
  shortName: string;
  utilities: string[];
  description?: string;
}

interface UpdatePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  activePlan: {
    id?: string | number;
    name: string;
    shortName: string;
    utilities: string[];
  };
  unitId?: string | number;
  onConfirmUpdate: (selectedPlan: SamplePlan) => void;
}

export const UpdatePlanModal: React.FC<UpdatePlanModalProps> = ({
  isOpen,
  onClose,
  activePlan,
  unitId,
  onConfirmUpdate,
}) => {
  const utilityId = useRemoteUtilityId();
  const [modalStep, setModalStep] = useState<1 | 2>(1);
  const [planSearch, setPlanSearch] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<SamplePlan | null>(null);

  const patchMutation = usePatchConfigMap();

  // Reset modal step and state whenever modal opens
  React.useEffect(() => {
    if (isOpen) {
      setModalStep(1);
      setSelectedPlan(null);
      setPlanSearch("");
    }
  }, [isOpen]);

  const { data: searchPlanData, isLoading } = usePlanSearch({
    remoteUtilityId: utilityId,
    searchQuery: planSearch,
    page: 1,
    limit: MODAL_PAGE_SIZE,
  });

  const apiPlans: SamplePlan[] = useMemo(() => {
    const rawList =
      searchPlanData?.result ||
      (Array.isArray(searchPlanData) ? searchPlanData : []);

    if (Array.isArray(rawList) && rawList.length > 0) {
      return rawList.map((p: any) => {
        const utilities: string[] = Array.isArray(p.utilities)
          ? p.utilities
          : Array.isArray(p.utilityService)
            ? p.utilityService
            : typeof p.utilityService === "string" && p.utilityService
              ? [p.utilityService]
              : typeof p.utilities === "string" && p.utilities
                ? [p.utilities]
                : [];

        return {
          id: String(p.id ?? ""),
          name: p.planName || p.name || "",
          shortName: p.shortName || "",
          utilities,
          description: p.description || p.planDescription || "",
        };
      });
    }

    return [];
  }, [searchPlanData]);

  const filteredPlans = useMemo(() => {
    if (!planSearch.trim()) return apiPlans;
    const query = planSearch.toLowerCase().trim();
    return apiPlans.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.shortName.toLowerCase().includes(query) ||
        p.utilities.some((u) => u.toLowerCase().includes(query)),
    );
  }, [apiPlans, planSearch]);

  const handleConfirm = () => {
    if (selectedPlan) {
      const planId = Number(selectedPlan.id) || selectedPlan.id;
      if (unitId) {
        const payload: any = {
          mapType: "TERRITORY_JSON",
          remote_utility_id: utilityId,
          entity_type: 11,
          code: unitId,
          plan_id: planId,
        };
        patchMutation.mutate(payload, {
          onSuccess: () => {
            onConfirmUpdate(selectedPlan);
          },
        });
      } else {
        onConfirmUpdate(selectedPlan);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-2">
            {modalStep === 2 && (
              <button
                onClick={() => setModalStep(1)}
                className="p-1 rounded-lg hover:bg-gray-200 text-gray-600 transition-colors mr-1 cursor-pointer"
                title="Back to Select Plan"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h3 className="text-base font-bold text-gray-900">
              {modalStep === 1 ? "Select New Plan" : "Confirm Plan Update"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {modalStep === 1 ? (
            /* STEP 1: SELECT PLAN */
            <>
              <p className="text-xs text-gray-500">
                Search and choose a plan from the list below to update the
                attached plan for this unit.
              </p>

              {/* Search Input */}
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  placeholder="Search plan by name, short code, or utility..."
                  value={planSearch}
                  onChange={(e) => setPlanSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-2xs"
                />
              </div>

              {/* Plan Cards Grid */}
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {isLoading ? (
                  <div className="py-8 text-center text-xs text-gray-400 font-medium">
                    Loading plans...
                  </div>
                ) : filteredPlans.length > 0 ? (
                  filteredPlans.map((plan) => {
                    const isSelected = selectedPlan?.id === plan.id;
                    return (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan)}
                        className={`p-4 rounded-xl border transition-all cursor-pointer space-y-2.5 ${
                          isSelected
                            ? "bg-blue-50/80 border-blue-500 ring-2 ring-blue-500/20 shadow-xs"
                            : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                                isSelected
                                  ? "border-blue-600 bg-blue-600 text-white"
                                  : "border-gray-300 bg-white"
                              }`}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 stroke-[3]" />
                              )}
                            </div>
                            <h4 className="text-sm font-bold text-gray-900">
                              {plan.name}
                            </h4>
                          </div>
                          {plan.shortName && (
                            <span className="text-xs font-mono text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded">
                              {plan.shortName}
                            </span>
                          )}
                        </div>

                        {plan.description && (
                          <p className="text-xs text-gray-500 pl-6">
                            {plan.description}
                          </p>
                        )}

                        {/* Utilities badges */}
                        <div className="flex flex-wrap items-center gap-1.5 pl-6 pt-0.5">
                          {plan.utilities.map((u, idx) => (
                            <span
                              key={idx}
                              className="text-[11px] font-medium px-2 py-0.5 bg-blue-100/80 text-blue-700 rounded-md"
                            >
                              {u}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="py-8 text-center text-xs text-gray-400 font-medium">
                    No matching plans found.
                  </div>
                )}
              </div>

              {/* Warning Message in Step 1 */}
              {selectedPlan && (
                <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-2.5">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <span className="font-bold text-xs capitalize tracking-wide text-amber-900 block">
                      Warning
                    </span>
                    <p className="text-xs font-semibold text-amber-800 leading-relaxed">
                      Consumer plan will also be updated with the new plan
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* STEP 2: CONFIRMATION SUMMARY */
            <>
              <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-2.5">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <span className="font-bold text-xs capitalize tracking-wide text-amber-900 block">
                    Warning
                  </span>
                  <p className="text-xs font-semibold text-amber-800 leading-relaxed">
                    Consumer plan will also be updated with the new plan
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-500 font-medium">
                Review the plan change details below before confirming the
                update.
              </p>

              {/* Old vs New Plan Comparison Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                {/* Old Plan */}
                <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/60 space-y-2">
                  <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">
                    Old Plan
                  </span>
                  <h4 className="text-sm font-bold text-gray-900">
                    {activePlan.name}
                  </h4>
                  {activePlan.shortName && (
                    <span className="text-xs font-mono text-gray-600 bg-gray-200/80 px-2 py-0.5 rounded inline-block">
                      {activePlan.shortName}
                    </span>
                  )}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {activePlan.utilities.map((u, idx) => (
                      <span
                        key={idx}
                        className="text-[11px] font-medium px-2 py-0.5 bg-gray-200 text-gray-700 rounded-md"
                      >
                        {u}
                      </span>
                    ))}
                  </div>
                </div>

                {/* New Selected Plan */}
                <div className="p-4 rounded-xl border border-blue-300 bg-blue-50/60 space-y-2 relative">
                  <span className="text-[11px] font-bold text-blue-600 uppercase tracking-wider block">
                    New Selected Plan
                  </span>
                  <h4 className="text-sm font-bold text-blue-950">
                    {selectedPlan?.name}
                  </h4>
                  {selectedPlan?.shortName && (
                    <span className="text-xs font-mono text-blue-700 bg-blue-100 px-2 py-0.5 rounded inline-block">
                      {selectedPlan.shortName}
                    </span>
                  )}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {selectedPlan?.utilities.map((u, idx) => (
                      <span
                        key={idx}
                        className="text-[11px] font-medium px-2 py-0.5 bg-blue-200/80 text-blue-800 rounded-md"
                      >
                        {u}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          {modalStep === 1 ? (
            <>
              <Button
                onClick={onClose}
                variant="outline"
                className="text-xs font-semibold border-gray-300 cursor-pointer"
              >
                Cancel
              </Button>
              <Button
                onClick={() => setModalStep(2)}
                disabled={!selectedPlan}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                Next: Summary
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => setModalStep(1)}
                variant="outline"
                className="text-xs font-semibold border-gray-300 flex items-center gap-1.5 cursor-pointer"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back to Select Plan
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={patchMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 cursor-pointer disabled:opacity-50"
              >
                {patchMutation.isPending ? "Updating..." : "Confirm & Update Plan"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
